import React from "react";
import {
  Link,
  NavLink,
  Route,
  Routes,
  useParams,
  Navigate,
} from "react-router-dom";
import { GuestResponse } from "./AdminGuests";
import { BASE_API_URL } from "store/Me";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import GuestInvites from "./GuestInvites";
import GuestCredentials from "./GuestCredentials";
import GuestKeyRequests from "./GuestKeyRequests";
import GuestKeyRings from "./GuestKeyRings";
import GuestLockActivity from "./GuestLockActivity";

const navLinks = [
  { to: "invites", label: "Invites" },
  { to: "credentials", label: "Credentials" },
  { to: "key-requests", label: "Key Requests" },
  { to: "lock-activity", label: "Lock Activity" },
];

export default function AdminGuest() {
  const { guestId } = useParams<{ guestId: string }>();
  const { data } = useGetGuest(guestId!);

  if (!data) return <div>loading...</div>;

  return (
    <div style={{ paddingTop: 36 }}>
      <Link to="/admin/guests">Back to Guests</Link>
      <Details guest={data} />
      <GuestKeyRings guestId={data.id} />
      <Navigation guestId={data.id} />
      <div style={{ paddingTop: 12 }}>
        <Routes>
          <Route path="invites" element={<GuestInvites guestId={data.id} />} />
          <Route
            path="credentials"
            element={<GuestCredentials guestId={data.id} />}
          />
          <Route
            path="key-requests"
            element={<GuestKeyRequests guestId={data.id} />}
          />
          <Route
            path="lock-activity"
            element={<GuestLockActivity guestId={data.id} />}
          />
          <Route path="*" element={<Navigate to="invites" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function Details({ guest }: { guest: GuestResponse }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <h4>
          {guest.firstName} {guest.lastName}
        </h4>
        <div
          style={{
            padding: "1px 4px",
            border: "1px solid",
            borderColor: guest.deletedTimestamp ? "red" : "green",
            color: guest.deletedTimestamp ? "red" : "green",
            backgroundColor: guest.deletedTimestamp ? "pink" : "lightgreen",
            fontWeight: 500,
            borderRadius: 4,
            fontSize: 12,
          }}
        >
          {guest.deletedTimestamp ? "Deleted" : "Active"}
        </div>
      </div>
      <p>
        <a href={`mailto:${guest.email}`}>{guest.email}</a>
      </p>
      <div style={{ fontSize: 12, color: "gray" }}>
        Valid {new Date(guest.validFrom).toLocaleString()} to{" "}
        {new Date(guest.validTo).toLocaleString()}
      </div>
    </div>
  );
}

function Navigation({ guestId }: { guestId: string }) {
  return (
    <div style={{ marginTop: 20 }}>
      <nav style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {navLinks.map(({ to, label }) => (
          <NavLink
            key={to}
            to={`/admin/guests/${guestId}/${to}`}
            className={({ isActive }) =>
              isActive ? "active-nav-link" : "inactive-nav-link"
            }
            style={({ isActive }) =>
              isActive
                ? {
                    fontWeight: 600,
                    backgroundColor: "rgb(95, 165, 249)",
                    padding: "4px 8px",
                    color: "white",
                    borderRadius: 4,
                    textDecoration: "none",
                  }
                : undefined
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

async function getGuest(guestId: string) {
  const url = `${BASE_API_URL}/api/guest/${guestId}`;
  const response = await axios.get<GuestResponse>(url);
  return response.data;
}

function useGetGuest(guestId: string) {
  return useQuery({
    queryKey: ["guests", "details", guestId],
    queryFn: () => getGuest(guestId),
  });
}
